import { ReactNode, useEffect, useRef, useState } from 'react'

import { useAuth } from '@/shared/auth'
import { Loader } from '@/shared/ui/loader'
import { EmailVerificationScreen } from '@/components/email-verification-screen'
import { LoginForm } from '@/components/login-form'
import { SignupForm } from '@/components/signup-form'
import { PasswordRecoveryForm } from '@/components/password-recovery-form'

type AuthView = 'login' | 'signup' | 'passwordRecovery'

export const WithAuthRequired = ({ children }: { children: ReactNode }) => {
    const { isLoadingUser, isLoggedIn, isEmailVerified, user } = useAuth()
    const [authView, setAuthView] = useState<AuthView>('login')
    const [isTransitioning, setIsTransitioning] = useState(false)
    const isMountedRef = useRef(true)

    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
        }
    }, [])

    // Reset authView to login when user logs out
    useEffect(() => {
        if (!isLoggedIn && !isLoadingUser) {
            setIsTransitioning(true)
            // Small delay to prevent abrupt unmounting; guard to avoid setState after unmount (fixes removeChild DOM error)
            const timeout = setTimeout(() => {
                if (isMountedRef.current) {
                    setAuthView('login')
                    setIsTransitioning(false)
                }
            }, 50)
            return () => clearTimeout(timeout)
        }
    }, [isLoggedIn, isLoadingUser])

    // Show loading while checking auth state or during transitions
    if (isLoadingUser || isTransitioning) {
        return <Loader />
    }

    // If user is not logged in, show auth modal
    if (!isLoggedIn || !user) {
        return (
            <div className='flex min-h-screen items-center justify-center bg-gray-50 p-4'>
                <div className='relative w-full max-w-md'>
                    {authView === 'login' && (
                        <LoginForm
                            onSwitchToSignup={() => setAuthView('signup')}
                            onSwitchToPasswordRecovery={() => setAuthView('passwordRecovery')}
                            onSuccess={() => {
                                // User logged in, component will re-render
                            }}
                        />
                    )}
                    {authView === 'signup' && (
                        <SignupForm
                            onSwitchToLogin={() => setAuthView('login')}
                            onSuccess={() => {
                                // User signed up, will show verification screen
                            }}
                        />
                    )}
                    {authView === 'passwordRecovery' && (
                        <PasswordRecoveryForm
                            onBackToLogin={() => setAuthView('login')}
                            onSwitchToSignup={() => setAuthView('signup')}
                        />
                    )}
                </div>
            </div>
        )
    }

    // If user is logged in but email not verified, show verification screen
    if (!isEmailVerified && user.providerData?.[0]?.providerId === 'password') {
        // Only require verification for email/password users, not for users logged in with secret
        return <EmailVerificationScreen />
    }

    // User is authenticated and verified, show the protected content.
    // Stable wrapper to avoid React DOM removeChild issues when switching from Loader/auth views to content.
    return <div className='contents'>{children}</div>
}

